import { supabase } from './supabaseClient';
import i18n from '../i18n';

/* ══ Mapping helpers ═══════════════════════════════════════════════
   The DB uses SQL-safe column names (date_label, description, …).
   The UI components keep using the original friendly field names
   (date, desc, cat, prayCount, …) — these helpers translate between
   the two so only this file needs to know about the DB schema. ══ */

const fromLocationRow = (r) => ({
  id: r.id,
  name: r.name,
  time: r.time,
  room: r.room,
  contact: r.contact,
  dept: r.dept,
  initial: r.initial,
  desc: r.description,
});
const toLocationRow = (f) => ({
  id: f.id,
  name: f.name,
  time: f.time,
  room: f.room,
  contact: f.contact,
  dept: f.dept,
  initial: f.initial,
  description: f.desc,
});

const fromEventRow = (r) => ({
  id: r.id,
  title: r.title,
  date: r.date_label,
  time: r.time_label,
  location: r.location,
  registered: r.registered,
  active: r.active,
  type: r.type,
  desc: r.description,
});
const toEventRow = (f) => ({
  id: f.id,
  title: f.title,
  date_label: f.date,
  time_label: f.time,
  location: f.location,
  registered: Number(f.registered) || 0,
  active: !!f.active,
  type: f.type,
  description: f.desc,
});

const fromNewsRow = (r) => ({
  id: r.id,
  title: r.title,
  date: r.date_label,
  excerpt: r.excerpt,
  status: r.status,
});
const toNewsRow = (f) => ({
  id: f.id,
  title: f.title,
  date_label: f.date,
  excerpt: f.excerpt,
  status: f.status,
});

const relativeDate = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return i18n.t('prayerWall.justNow');
  if (mins < 60) return i18n.t('prayerWall.minutesAgo', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return i18n.t('prayerWall.hoursAgo', { count: hrs });
  const days = Math.floor(hrs / 24);
  return i18n.t('prayerWall.daysAgo', { count: days });
};

const fromPrayerRow = (r) => ({
  id: r.id,
  text: r.text,
  author: r.author,
  cat: r.category,
  approved: r.approved,
  prayCount: r.pray_count,
  date: relativeDate(r.created_at),
});

const MAPPERS = {
  locations: { from: fromLocationRow, to: toLocationRow, order: 'sort_order' },
  events: { from: fromEventRow, to: toEventRow, order: 'created_at' },
  news: { from: fromNewsRow, to: toNewsRow, order: null },
};

/* ══ Generic content (locations / events / news) ══════════════════ */

export async function listContent(type) {
  const m = MAPPERS[type];
  let q = supabase.from(type).select('*');
  if (m.order) q = q.order(m.order, { ascending: true });
  const { data, error } = await q;
  if (error) throw error;
  return data.map(m.from);
}

export async function upsertContent(type, form) {
  const m = MAPPERS[type];
  const row = m.to(form);
  const { data, error } = await supabase
    .from(type)
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return m.from(data);
}

export async function deleteContent(type, id) {
  const { error } = await supabase.from(type).delete().eq('id', id);
  if (error) throw error;
}

/* ══ Prayers ═══════════════════════════════════════════════════════ */

export async function listApprovedPrayers() {
  const { data, error } = await supabase
    .from('prayers')
    .select('*')
    .eq('approved', true)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data.map(fromPrayerRow);
}

export async function listAllPrayers() {
  const { data, error } = await supabase
    .from('prayers')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data.map(fromPrayerRow);
}

export async function submitPrayer({ id, text, author, cat, userId }) {
  const { data, error } = await supabase
    .from('prayers')
    .insert({
      id,
      text,
      author: author || '',
      category: cat,
      user_id: userId,
      approved: false,
      pray_count: 0,
    })
    .select()
    .single();
  if (error) throw error;
  return fromPrayerRow(data);
}

export async function approvePrayer(id) {
  const { error } = await supabase
    .from('prayers')
    .update({ approved: true })
    .eq('id', id);
  if (error) throw error;
}

export async function deletePrayer(id) {
  const { error } = await supabase.from('prayers').delete().eq('id', id);
  if (error) throw error;
}

// Returns the new "liked" state (true/false) — pray_count is updated
// atomically server-side, so callers should re-fetch/patch prayCount.
export async function togglePrayerLike(prayerId) {
  const { data, error } = await supabase.rpc('toggle_prayer_like', {
    p_prayer_id: prayerId,
  });
  if (error) throw error;
  return data;
}

export async function listMyPrayerLikes(userId) {
  const { data, error } = await supabase
    .from('prayer_likes')
    .select('prayer_id')
    .eq('user_id', userId);
  if (error) throw error;
  return data.map((r) => r.prayer_id);
}

/* ══ Site settings (single 'global' row) ══════════════════════════ */

const fromSettingsRow = (r) => ({
  memberCount: r.member_count,
  showDonations: r.show_donations,
  themeIdx: r.theme_idx,
});

export async function getSettings() {
  const { data, error } = await supabase
    .from('site_settings')
    .select('*')
    .eq('id', 'global')
    .single();
  if (error) throw error;
  return fromSettingsRow(data);
}

export async function updateSettings({ memberCount, showDonations, themeIdx }) {
  const row = {};
  if (memberCount !== undefined) row.member_count = Number(memberCount) || 0;
  if (showDonations !== undefined) row.show_donations = !!showDonations;
  if (themeIdx !== undefined) row.theme_idx = Number(themeIdx) || 0;
  const { data, error } = await supabase
    .from('site_settings')
    .update(row)
    .eq('id', 'global')
    .select()
    .single();
  if (error) throw error;
  return fromSettingsRow(data);
}
